import { Box } from '@mui/material';
import { Route, Switch } from 'react-router-dom';
import './ccf-route-mapper.module.scss';
import { CcfProtectedRoute } from '../ccf-protected-route/ccf-protected-route';
import { Fragment } from 'react';
/* eslint-disable-next-line */

export interface RouteInterface {
  path: string;
  private: boolean;
  component?: React.ReactNode;
  children?: RouteInterface[];
  navComponent?: React.ReactNode;
  headerComponent?: React.ReactNode;
}

export interface CcfRouteMapperProps {
  routes: RouteInterface[];
  children?: React.ReactNode;
}

/**
 * function to map different routes
 * @param param0 - RouteInterface
 * @example - <RouteMapperTemplate />
 * @returns
 */
const RouteMapperTemplate = ({ route }: { route: RouteInterface }) => {
  return (
    <Box height="100%" flex="1">
      {route.headerComponent}
      <Box
        display="flex"
        flex="1"
        height={route.headerComponent ? 'calc( 100% - 56px )' : '100%'}
      >
        {route.navComponent}
        {route.component}
      </Box>
    </Box>
  );
};

/**
 * Function to map nested route
 * @param param0 - RouteInterface
 * @example <NestedRouteMapperTemplate />
 * @returns
 */
const NestedRouteMapperTemplate = ({ route }: { route: RouteInterface }) => {
  return (
    <Box height="100%" flex="1">
      {route.headerComponent}
      <Box
        display="flex"
        flex="1"
        bgcolor={`var(--color-landing-background)`}
        height={route.headerComponent ? 'calc( 100% - 57px )' : '100%'}
      >
        {route.navComponent}
        <CcfRouteMapper routes={route.children as RouteInterface[]}>
          {route.component}
        </CcfRouteMapper>
      </Box>
    </Box>
  );
};

/**
 * Component to map diiferent routes
 * @param param0 - CcfRouteMapperProps
 * @example - <CcfRouteMapper />
 * @returns
 */
export function CcfRouteMapper({ routes, children }: CcfRouteMapperProps) {
  /**
   * function to render route
   * @param route - RouteInterface
   * @param index - number
   * @example renderRoute()
   * @returns
   */
  const renderRoute = (route: RouteInterface, index: number) => {
    if (route.children) {
      if (route.private) {
        return (
          <CcfProtectedRoute
            path={route.path}
            key={`route-${index}-${route.path}`}
          >
            <NestedRouteMapperTemplate route={route} />
          </CcfProtectedRoute>
        );
      }
      return (
        <Route path={route.path} key={`route-${index}-${route.path}`}>
          <NestedRouteMapperTemplate route={route} />
        </Route>
      );
    }

    if (route.private) {
      return (
        <CcfProtectedRoute
          exact
          path={route.path}
          key={`route-${index}-${route.path}`}
        >
          <RouteMapperTemplate route={route} />
        </CcfProtectedRoute>
      );
    }
    return (
      <Route exact path={route.path} key={`route-${index}-${route.path}`}>
        <RouteMapperTemplate route={route} />
      </Route>
    );
  };

  return (
    <Fragment>
      {children}
      <Switch>
        {routes.map((route: RouteInterface, index: number) =>
          renderRoute(route, index)
        )}
      </Switch>
    </Fragment>
  );
}

export default CcfRouteMapper;
